import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { api } from "@/lib/api";
import { LayoutDashboard, Upload, ShoppingCart, Timer, DollarSign, LineChart, MessageSquareText, ClipboardList, CreditCard, FileCode2, BellRing, Search } from "lucide-react";

const PAGES = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/upload", label: "Uploads", icon: Upload },
  { to: "/app/reorder", label: "Reorder", icon: ShoppingCart },
  { to: "/app/slow", label: "Slow Inventory", icon: Timer },
  { to: "/app/pricing", label: "Pricing intelligence", icon: DollarSign },
  { to: "/app/market", label: "Market comparison", icon: LineChart },
  { to: "/app/recommendations", label: "Recommendations", icon: ClipboardList },
  { to: "/app/ask", label: "Ask TreadMargin", icon: MessageSquareText },
  { to: "/app/settings/billing", label: "Billing", icon: CreditCard },
  { to: "/app/settings/templates", label: "Mapping templates", icon: FileCode2 },
  { to: "/app/settings/preferences", label: "Digest preferences", icon: BellRing },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.get("/analytics/items", { params: { view: "all", demo: false } })
        .then((r) => setItems((r.data.items || []).slice(0, 500)))
        .catch(() => setItems([]));
    }, 100);
  }, [open]);

  const filteredSkus = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return items.slice(0, 8);
    return items.filter((i) => `${i.sku} ${i.brand} ${i.tire_size}`.toLowerCase().includes(q)).slice(0, 8);
  }, [items, query]);

  const go = (to) => { setOpen(false); nav(to); };
  const askThis = () => { setOpen(false); nav(`/app/ask`, { state: { prefill: query } }); };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen} title="Command palette" description="Search SKUs, jump to a page, or ask TreadMargin.">
        <CommandInput placeholder="Search SKUs, pages, or ask a question…" value={query} onValueChange={setQuery} data-testid="palette-input" />
        <CommandList data-testid="palette-list">
          <CommandEmpty>No matches. Press Enter to Ask TreadMargin instead.</CommandEmpty>
          {query.trim() && (
            <>
              <CommandGroup heading="Ask TreadMargin">
                <CommandItem onSelect={askThis} data-testid="palette-ask">
                  <MessageSquareText className="mr-2 h-4 w-4" /> Ask: "{query}"
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
            </>
          )}
          {filteredSkus.length > 0 && (
            <CommandGroup heading="Inventory">
              {filteredSkus.map((i, idx) => (
                <CommandItem key={`${i.sku}-${idx}`} value={`sku-${i.sku}-${idx}`} onSelect={() => go(`/app/reorder?sku=${encodeURIComponent(i.sku)}`)} data-testid={`palette-sku-${i.sku}`}>
                  <Search className="mr-2 h-4 w-4 text-[#3B424B]" />
                  <span className="font-mono text-xs mr-2">{i.sku}</span>
                  <span className="text-[#3B424B] text-xs">{i.brand} · {i.tire_size}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandGroup heading="Pages">
            {PAGES.filter((p) => !query.trim() || p.label.toLowerCase().includes(query.toLowerCase())).map((p) => (
              <CommandItem key={p.to} onSelect={() => go(p.to)} data-testid={`palette-page-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p.icon className="mr-2 h-4 w-4" /> {p.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      <button aria-label="Open command palette" onClick={() => setOpen(true)} data-testid="palette-trigger" className="fixed bottom-5 right-5 z-40 h-11 px-4 rounded-full bg-[var(--tm-charcoal)] text-white text-sm font-medium shadow-lg hover:shadow-xl transition-shadow inline-flex items-center gap-2 lg:hidden">
        <Search size={15} /> Search
      </button>
    </>
  );
}
